import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { Container, Row, Col, Button } from 'react-bootstrap';
import Navbar from '../../Pages/Navbar/JobSeekerNavbar';
import Footer from "../../Footer/Footer";
import Swal from 'sweetalert2';
import BaseUrl from '../../ApiServices/BaseUrl';

function JobDetails() {
    const navigate = useNavigate();
    const { state } = useLocation();
    const { jobId } = useParams();
    const user = JSON.parse(localStorage.getItem('user')) || {};
    const [job, setJob] = useState(state?.job || null);
    const [applied, setApplied] = useState(false);
    const [loading, setLoading] = useState(true);
    const [showRegisterPopup, setShowRegisterPopup] = useState(false);
    const [showLoginPopup, setShowLoginPopup] = useState(false);
    const [hasResume, setHasResume] = useState(false);
    const [relatedJobs, setRelatedJobs] = useState([]);
    const [showReportForm, setShowReportForm] = useState(false);
    const [reportData, setReportData] = useState({
        email: user.email || '',
        reason: '',
        description: ''
    });
    const [alreadyReported, setAlreadyReported] = useState(false);

    const safeParseArray = (value) => {
        try {
            if (!value) return [];
            if (Array.isArray(value)) return value;
            return JSON.parse(value);
        } catch (e) {
            return [];
        }
    };

    useEffect(() => {
        const fetchJobDetails = async () => {
            try {
                // If job not passed via state, fetch it by ID
                if (!job && jobId) {
                    const response = await fetch(`${BaseUrl}/api/employer/${jobId}`);
                    if (!response.ok) {
                        throw new Error('Failed to fetch job details');
                    }
                    const data = await response.json();
                    setJob(data);
                }

                // Check if user has applied and has resume
                if (user.id) {
                    // Check application status
                    const applicationResponse = await fetch(`${BaseUrl}/api/applications?userid=${user.id}&jobid=${jobId || job?.id}`);
                    const applicationData = await applicationResponse.json();
                    if (applicationResponse.ok && applicationData.length > 0) {
                        setApplied(true);
                    }

                    // Check resume status by fetching all job seekers and checking if current user exists
                    const resumeResponse = await fetch(`${BaseUrl}/api/job-seeker`);
                    const resumeData = await resumeResponse.json();
                    const userHasResume = resumeData.some(seeker => seeker.user_id === user.id);
                    setHasResume(userHasResume);

                    // Check if user has already reported this job
                    const reportResponse = await fetch(`${BaseUrl}/api/jobreport`);
                    const reportData = await reportResponse.json();
                    if (reportResponse.ok) {
                        const hasReported = reportData.some(
                            report => report.user_id === user.id && report.job_id === (jobId || job?.id)
                        );
                        setAlreadyReported(hasReported);
                    }
                }
            } catch (err) {
                console.error('Error fetching data:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchJobDetails();
    }, [jobId, job, user.id]);

    // Check if employer has Gold subscription
const hasGoldSubscription = (job) => {
    const planName = job.plan_name?.toLowerCase();
    return planName?.includes('gold');
};

// Check if employer has Silver subscription
const hasSilverSubscription = (job) => {
    const planName = job.plan_name?.toLowerCase();
    return planName?.includes('silver');
};

    useEffect(() => {
        const fetchRelatedJobs = async () => {
            if (!job?.domestic_worker_category) return;

            try {
                const response = await fetch(`${BaseUrl}/api/employer/all`);
                if (!response.ok) {
                    throw new Error('Failed to fetch jobs');
                }
                let data = await response.json();

                // Filter jobs where status is 'Approved' and category matches
                data = data.filter(relatedJob =>
                    relatedJob.status === 'Approved' &&
                    relatedJob.domestic_worker_category === job.domestic_worker_category &&
                    relatedJob.id !== (jobId || job?.id) // Exclude current job
                ).slice(0, 4); // Limit to 4 jobs

                // Safely parse relevant fields
                data = data.map(relatedJob => ({
                    ...relatedJob,
                    preferred_candidates_country: safeParseArray(relatedJob.preferred_candidates_country),
                    candidates_country_experience: safeParseArray(relatedJob.candidates_country_experience),
                    main_skills: safeParseArray(relatedJob.main_skills),
                    cooking_skills: safeParseArray(relatedJob.cooking_skills),
                    other_skills: safeParseArray(relatedJob.other_skills),
                    preferred_years_of_experience: safeParseArray(relatedJob.preferred_years_of_experience),
                    offer_for_selected_candidates: safeParseArray(relatedJob.offer_for_selected_candidates),
                    preferred_language_for_worker: safeParseArray(relatedJob.preferred_language_for_worker),
                    profile_photo_url: job.profile_photo
                        ? `${BaseUrl}${job.profile_photo}`
                        : null
                }));

                setRelatedJobs(data);
            } catch (err) {
                console.error('Error fetching related jobs:', err);
            }
        };

        if (job) {
            fetchRelatedJobs();
        }
    }, [job, jobId]);

    const handleBackToList = () => {
        navigate('/viewjobs');
    };

    const handleApply = async () => {
        if (!user.id) {
            Swal.fire({
                title: 'Registration Required',
                text: 'You need to register or login to apply for this job',
                icon: 'info',
                showCancelButton: true,
                confirmButtonText: 'Register',
                cancelButtonText: 'Login',
                customClass: {
                    popup: 'registration-popup'
                }
            }).then((result) => {
                if (result.isConfirmed) {
                    navigate('/register');
                } else if (result.dismiss === Swal.DismissReason.cancel) {
                    navigate('/login');
                }
            });
            return;
        }

        // Check if user has resume
        try {
            const resumeResponse = await fetch(`${BaseUrl}/api/job-seeker`);
            const resumeData = await resumeResponse.json();
            const userHasResume = resumeData.some(seeker => seeker.user_id === user.id);

            if (!userHasResume) {
                Swal.fire({
                    title: 'Resume Required',
                    text: 'You need to update your resume before applying for jobs',
                    icon: 'info',
                    confirmButtonText: 'Update resume',
                    confirmButtonColor: '#28a745'
                }).then((result) => {
                    if (result.isConfirmed) {
                        navigate('/personalinfo');
                    }
                });
                return;
            }

            // Show confirmation dialog before applying
            const result = await Swal.fire({
                title: 'Are you sure?',
                text: 'You are about to apply for this job',
                icon: 'question',
                showCancelButton: true,
                confirmButtonColor: '#28a745',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Yes, apply!',
                cancelButtonText: 'Cancel'
            });

            if (!result.isConfirmed) {
                return;
            }

            // Proceed with application
            const payload = {
                userid: user.id,
                empuserid: job.user_id,
                jobid: job.id,
            };

            const response = await fetch(`${BaseUrl}/api/applications`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            const data = await response.json();

            if (response.ok) {
                Swal.fire({
                    icon: 'success',
                    title: 'Success!',
                    text: 'You have successfully applied for this job',
                    confirmButtonColor: '#28a745'
                });
                setApplied(true);
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: data.error || 'Failed to apply for job',
                    confirmButtonColor: '#d33'
                });
            }
        } catch (err) {
            console.error('Error while applying:', err);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'An error occurred while applying: ' + err.message,
                confirmButtonColor: '#d33'
            });
        }
    };

    const toggleReportForm = () => {
        if (alreadyReported) return;

        setShowReportForm(!showReportForm);
        // Reset form when opening
        if (!showReportForm) {
            setReportData({
                email: user.email || '',
                reason: '',
                description: ''
            });
        }
    };

    const handleReportChange = (e) => {
        const { name, value } = e.target;
        setReportData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleReportSubmit = async (e) => {
        e.preventDefault();

        if (!user.id) {
            Swal.fire({
                title: 'Login Required',
                text: 'You need to login to report this job',
                icon: 'info',
                confirmButtonText: 'Login'
            }).then(() => {
                setShowLoginPopup(true);
            });
            return;
        }

        try {
            const payload = {
                user_id: user.id,
                first_name: user.first_name || 'Anonymous',
                job_id: job.id,
                emp_id: job.user_id,
                email: reportData.email,
                reason: reportData.reason,
                description: reportData.description
            };

            const response = await fetch(`${BaseUrl}/api/jobreport`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            if (response.ok) {
                Swal.fire({
                    icon: 'success',
                    title: 'Report Submitted',
                    text: 'Thank you for reporting this job. Our team will review it shortly.',
                    confirmButtonColor: '#28a745'
                });
                setShowReportForm(false);
                setAlreadyReported(true);
                setReportData({
                    email: user.email || '',
                    reason: '',
                    description: ''
                });
            } else {
                const data = await response.json();
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: data.error || 'Failed to submit report',
                    confirmButtonColor: '#d33'
                });
            }
        } catch (err) {
            console.error('Error submitting report:', err);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'An error occurred while submitting your report',
                confirmButtonColor: '#d33'
            });
        }
    };

    const closeRegisterPopup = () => {
        setShowRegisterPopup(false);
    };

    const closeLoginPopup = () => {
        setShowLoginPopup(false);
    };

    const handleLoginSuccess = async () => {
        closeLoginPopup();
        const updatedUser = JSON.parse(localStorage.getItem('user')) || {};

        setLoading(true);
        try {
            if (updatedUser.id && job?.id) {
                // Check application status
                const applicationResponse = await fetch(`${BaseUrl}/api/applications?userid=${updatedUser.id}&jobid=${job.id}`);
                const applicationData = await applicationResponse.json();
                if (applicationResponse.ok && applicationData.length > 0) {
                    setApplied(true);
                }

                // Check resume status by fetching all job seekers and checking if current user exists
                const resumeResponse = await fetch(`${BaseUrl}/api/job-seeker`);
                const resumeData = await resumeResponse.json();
                const userHasResume = resumeData.some(seeker => seeker.user_id === updatedUser.id);
                setHasResume(userHasResume);

                // Check if user has already reported this job
                const reportResponse = await fetch(`${BaseUrl}/api/jobreport`);
                const reportData = await reportResponse.json();
                if (reportResponse.ok) {
                    const hasReported = reportData.some(
                        report => report.user_id === updatedUser.id && report.job_id === job.id
                    );
                    setAlreadyReported(hasReported);
                }
            }
        } catch (err) {
            console.error('Error checking application status:', err);
        } finally {
            setLoading(false);
        }
    };

    const safeJoin = (array, separator = ', ') => {
        if (!array) return 'N/A';
        return Array.isArray(array) ? array.join(separator) : array;
    };

   const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    try {
        // First, check if it's already a valid date
        let date = new Date(dateString);
        
        // If it's invalid, try to parse it differently
        if (isNaN(date.getTime())) {
            // Try to parse various date formats
            if (dateString.includes('-')) {
                // If it's in YYYY-MM-DD format without time
                if (!dateString.includes('T') && !dateString.includes(' ')) {
                    date = new Date(dateString + 'T00:00:00');
                } else {
                    // Try to parse as-is
                    date = new Date(dateString);
                }
            } else {
                // Try to parse the string directly
                date = new Date(dateString);
            }
        }
        
        // Check if it's valid after parsing
        if (isNaN(date.getTime())) {
            return 'N/A';
        }
        
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    } catch (e) {
        console.error('Error formatting date:', e);
        return 'N/A';
    }
};

    const handleBack = () => {
        navigate('/appliedjobs');
    };

    if (!job) {
        return (
            <>
                <Navbar />
                <div className="text-center mt-5" style={{ marginTop: '100px' }}>
                    <div className="text-center">Job details not found</div>
                    <button
                        className="btn btn-outline-secondary mt-3"
                        onClick={handleBackToList}
                    >
                        Back to List
                    </button>
                </div>
            </>
        );
    }

    return (
        <>
            <Navbar />
            <section className="agency blog blog-sec blog-sidebar pb-5 blog-list sider" style={{ marginTop: '80px' }}>
                <Container>
                    <Row>
                        <Col md={12} lg={9} className="pe-lg-4">
                            <div className="product-wrapper-grid list-view">
                                <Row>
                                    <Col xs={12} className="col-12_padding_Set">
                                        <div className="agency-box position-relative">
                                            <div className="agency-header-container">
                                                <picture>
                                                    <source media="(max-width:576px)" srcSet="https://cdn.helperplace.com/front-app/assets/images/misc/5-sm.webp" />
                                                    <source media="(max-width:768px)" srcSet="https://cdn.helperplace.com/front-app/assets/images/misc/5-md.webp" />
                                                    <img
                                                        src="https://cdn.helperplace.com/front-app/assets/images/misc/5.jpg"
                                                        alt="Header"
                                                        className="agency_header"
                                                    />
                                                </picture>
                                                <div className="agency_header_White_opcity"></div>

                                                <div className="profile-section">
                                                    <div className="profile-image-wrapper">
                                                        <img
                                                            src={job.profile_photo_url || '/fallback-user.png'}
                                                            alt="Profile"
                                                            className="profile-image"
                                                        />
                                                    </div>

                                                    <div className="badge-group">
                                                        <label className="label_blue">
                                                            {job.posted_by?.toLowerCase().includes('direct') ? 'Direct' : 'Agency'}
                                                        </label>
                                                        <span className="top-active">Very Active</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="product-box">
                                                <div className="d-flex justify-content-between align-items-start" style={{ width: '100%' }}>
                                                    <div className="flex-grow-1">
                                                        <h1 className="mb-0 listing-about-title p-0 mobile-job-title">
                                                            {job.job_title}
                                                        </h1>
                                                        <div className="hp-candidate-wrapper mobile-candidate-wrapper">
                                                            <div className="candidate-info">
                                                                <p className="mb-2 p-0 listing-about-sub-title mobile-job-subtitle">
                                                                    {job.adults || 'N/A'} adults + {job.children || 'N/A'} kids | {job.nationality || 'N/A'}
                                                                    <span> | Posted: {formatDate(job.created_at)} </span>
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="apply-btn-container ms-3" >
                                                        <div className="jobdetails-applybuttons mobile-buttons-container gap-2">

                                                            <Button
                                                                variant="secondary"
                                                                className="hp-cancel-jobdetails mobile-cancel-btn"
                                                                onClick={handleBack}
                                                            >
                                                                Back
                                                            </Button>
                                                            <Button
                                                                variant=""
                                                                className="hp-apply-jobdetails mobile-apply-btn"
                                                                onClick={handleApply}
                                                                disabled={applied || loading}
                                                            >
                                                                {loading ? 'Checking...' : (applied ? 'Applied' : 'Apply')}
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </Col>
                                </Row>

                                {/* Job Requirement Section */}
                                <Row className="custom-header-box">
                                    <Col className="mx-auto">
                                        <h2 className="text-light ar">Job Requirement</h2>
                                    </Col>
                                </Row>
                                <Row className="job-requirement">
                                    <Col className="mx-auto">
                                        <Row className="ar">
                                            <Col xs={12} sm={12} md={6} lg={6} xl={6}>
                                                <h3 className="footer-experience">
                                                    <svg className="icons" height="17" width="17">
                                                        <use height="14" width="14" href="https://www.helperplace.com/assets/icons/helperplace_all_icons.svg#user"></use>
                                                    </svg>
                                                    {job.domestic_worker_category || 'N/A'} <span> |</span>
                                                    <span> {job.job_type || 'N/A'} </span>
                                                </h3>
                                            </Col>
                                            <Col xs={12} sm={12} md={6} lg={6} xl={6}>
                                                <h3 className="footer-experience">
                                                    <svg className="icons" height="17" width="17">
                                                        <use height="14" width="14" href="https://www.helperplace.com/assets/icons/helperplace_all_icons.svg#calendar"></use>
                                                    </svg>
                                                    Start Before: {formatDate(job.job_starting_date)}
                                                </h3>
                                            </Col>
                                            <Col xs={12} sm={12} md={6} lg={6} xl={6}>
                                                <h3 className="footer-experience">
                                                    <svg className="icons" height="17" width="17">
                                                        <use height="14" width="14" href="https://www.helperplace.com/assets/icons/helperplace_all_icons.svg#map"></use>
                                                    </svg>
                                                    {job.working_city || 'N/A'}, {job.state_or_province || 'N/A'}
                                                </h3>
                                            </Col>
                                            <Col xs={12} sm={12} md={6} lg={6} xl={6}>
                                                <h3 className="footer-experience">
                                                    <svg className="icons" height="17" width="17">
                                                        <use height="16" width="16" href="https://www.helperplace.com/assets/icons/helperplace_all_icons.svg#file"></use>
                                                    </svg>
                                                    Contract Status: {job.contract_status || 'N/A'}
                                                </h3>
                                            </Col>
                                            <Col xs={12} sm={12} md={6} lg={6} xl={6}>
                                                <h3 className="footer-experience">
                                                    <svg className="icons" height="17" width="17">
                                                        <use height="16" width="16" href="https://www.helperplace.com/assets/icons/helperplace_all_icons.svg#bed-person"></use>
                                                    </svg>
                                                    Accommodation: {job.accommodation_type || 'N/A'}
                                                </h3>
                                            </Col>
                                            <Col xs={12} sm={12} md={6} lg={6} xl={6}>
                                                <h3 className="footer-experience">
                                                    <svg className="icons" height="17" width="17">
                                                        <use height="15" width="15" href="https://www.helperplace.com/assets/icons/helperplace_all_icons.svg#day_off"></use>
                                                    </svg>
                                                    Day Off: <span>{job.day_off || 'N/A'}</span>
                                                </h3>
                                            </Col>
                                        </Row>
                                    </Col>
                                </Row>

                                {/* Job Description Section */}
                                <Row className="custom-header-box">
                                    <Col className="mx-auto">
                                        <h2 className="text-light ar">Job Description</h2>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col className="mx-auto mt-3 jOb_description description_tag ar">
                                        <div style={{ wordBreak: 'break-word' }}>
                                            {job.job_description}
                                        </div>
                                    </Col>
                                </Row>

                                {/* Required Skills Section */}
                                <Row className="custom-header-box">
                                    <Col className="mx-auto">
                                        <h2 className="text-light ar">Required Skills / Duties</h2>
                                    </Col>
                                </Row>
                                <Row className="yoga mt-3 event">
                                    {/* Language Skills */}
                                    <Col className="ar">
                                        <div className="col-11 phone_pedding mx-auto">
                                            <div className="event-container event-container_footer_margin_custom d-flex">
                                                <div className="yoga-circle">
                                                    <picture>
                                                        <source width="35px" height="35px" media="(max-width:992px)" srcSet="https://cdn.helperplace.com/skill_cat/1_1599642484.webp" />
                                                        <img width="40px" height="40px" src="https://cdn.helperplace.com/skill_cat/1_1599642484.webp" alt="Language skills" />
                                                    </picture>
                                                </div>
                                                <div className="event-info">
                                                    <h3 className="primary ar"> Language: </h3>
                                                    {job.preferred_language_for_worker?.length > 0 ? (
                                                        job.preferred_language_for_worker.map((lang, index) => (
                                                            <h4 key={index} className="float-left float-left-ar color_1"> {lang} </h4>
                                                        ))
                                                    ) : (
                                                        <h4 className="float-left float-left-ar color_1">N/A</h4>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </Col>

                                    {/* Main Skills */}
                                    <Col xs={11} className="ar">
                                        <div className="col-11 phone_pedding mx-auto">
                                            <div className="event-container event-container_footer_margin_custom d-flex">
                                                <div className="yoga-circle">
                                                    <picture>
                                                        <source width="35px" height="35px" media="(max-width:992px)" srcSet="https://cdn.helperplace.com/skill_cat/4_1599643968.webp" />
                                                        <img width="40px" height="40px" src="https://cdn.helperplace.com/skill_cat/4_1599643968.webp" alt="Main skills" />
                                                    </picture>
                                                </div>
                                                <div className="event-info">
                                                    <h3 className="primary ar"> Main Skills: </h3>
                                                    {job.main_skills?.length > 0 ? (
                                                        job.main_skills.map((skill, index) => (
                                                            <h4 key={index} className="float-left float-left-ar color_2"> {skill} </h4>
                                                        ))
                                                    ) : (
                                                        <h4 className="float-left float-left-ar color_2">N/A</h4>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </Col>

                                    {/* Cooking Skills */}
                                    <Col xs={11} className="ar">
                                        <div className="col-11 phone_pedding mx-auto">
                                            <div className="event-container event-container_footer_margin_custom d-flex">
                                                <div className="yoga-circle">
                                                    <picture>
                                                        <source width="35px" height="35px" media="(max-width:992px)" srcSet="https://cdn.helperplace.com/skill_cat/2_1599644151.webp" />
                                                        <img width="40px" height="40px" src="https://cdn.helperplace.com/skill_cat/2_1599644151.webp" alt="Cooking skills" />
                                                    </picture>
                                                </div>
                                                <div className="event-info">
                                                    <h3 className="primary ar"> Cooking Skills: </h3>
                                                    {job.cooking_skills?.length > 0 ? (
                                                        job.cooking_skills.map((skill, index) => (
                                                            <h4 key={index} className="float-left float-left-ar color_3"> {skill} </h4>
                                                        ))
                                                    ) : (
                                                        <h4 className="float-left float-left-ar color_3">N/A</h4>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </Col>

                                    {/* Other Skills */}
                                    <Col xs={11} className="ar">
                                        <div className="col-11 phone_pedding mx-auto">
                                            <div className="event-container event-container_footer_margin_custom d-flex">
                                                <div className="yoga-circle">
                                                    <picture>
                                                        <source width="35px" height="35px" media="(max-width:992px)" srcSet="https://cdn.helperplace.com/skill_cat/3_1599642528.webp" />
                                                        <img width="40px" height="40px" src="https://cdn.helperplace.com/skill_cat/3_1599642528.webp" alt="Other skills" />
                                                    </picture>
                                                </div>
                                                <div className="event-info">
                                                    <h3 className="primary ar"> Other Skills: </h3>
                                                    {job.other_skills?.length > 0 ? (
                                                        job.other_skills.map((skill, index) => (
                                                            <h4 key={index} className="float-left float-left-ar color_4"> {skill} </h4>
                                                        ))
                                                    ) : (
                                                        <h4 className="float-left float-left-ar color_4">N/A</h4>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </Col>
                                </Row>

                                {alreadyReported ? (
                                    <span className="float-right float-left-ar primary mr-2 ml-2">
                                        This job already reported
                                    </span>
                                ) : (
                                    <span
                                        className="float-right float-left-ar primary pointer mr-2 ml-2"
                                        onClick={toggleReportForm}
                                    >
                                        Report this Job
                                    </span>
                                )}

                                {/* Report Job Form */}
                                {showReportForm && (
                                    <div className="report-job-section p-3">
                                        <div className="shadow p-4" style={{ width: '100%' }}>
                                            <h5 className="mb-3">Report this job ad</h5>

                                            <form onSubmit={handleReportSubmit}>
                                                <div className="mb-3">
                                                    <label htmlFor="email" className="form-label">
                                                        Your email address
                                                    </label>
                                                    <input
                                                        type="email"
                                                        className="form-control report-job-input"
                                                        id="email"
                                                        name="email"
                                                        value={reportData.email}
                                                        onChange={handleReportChange}
                                                        placeholder="Enter your email"
                                                        required
                                                    />
                                                </div>

                                                <div className="mb-3">
                                                    <label htmlFor="reason" className="form-label">
                                                        Reason for reporting job
                                                    </label>
                                                    <select
                                                        className="form-select report-job-select"
                                                        id="reason"
                                                        name="reason"
                                                        value={reportData.reason}
                                                        onChange={handleReportChange}
                                                        required
                                                    >
                                                        <option value="">Please select</option>
                                                        <option value="Fraudulent">Fraudulent</option>
                                                        <option value="Misleading">Misleading</option>
                                                        <option value="Discriminatory">Discriminatory</option>
                                                        <option value="Other">Other</option>
                                                    </select>
                                                </div>

                                                <div className="mb-3">
                                                    <label htmlFor="description" className="form-label">
                                                        Please describe your issues with this listing
                                                    </label>
                                                    <textarea
                                                        className="form-control report-job-textarea"
                                                        id="description"
                                                        name="description"
                                                        value={reportData.description}
                                                        onChange={handleReportChange}
                                                        rows="4"
                                                        required
                                                    ></textarea>
                                                </div>

                                                <div className="report-job-warning mb-3">
                                                    ⚠️ To help fast track investigation, please include here any other relevant details that prompted you to report this job ad as fraudulent / misleading / discriminatory.
                                                </div>

                                                <div className="d-flex flex-column my-3">
                                                    <button type="submit" className="btn btn-primary report-job-button mb-3">
                                                        Report job
                                                    </button>

                                                    <button
                                                        type="button"
                                                        className="btn btn-link report-job-cancel"
                                                        onClick={toggleReportForm}
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            </form>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </Col>

                        {/* Sidebar with Related Jobs */}
                        {/* Sidebar with Related Jobs */}
                        <Col md={12} lg={3} className="collection-filter-block resume-right-block">
                            <div className="sticky-top" style={{ top: '90px' }}>
                                <h4 className="mb-4 similarjobs-heading">Similar Jobs</h4>
                                {relatedJobs.length > 0 ? (
                                    relatedJobs.map((relatedJob) => (
                                        <div key={relatedJob.id} className="mb-4">
                                            <div
                                                className="jobcard-wrapper-viewjobs d-flex p-3 shadow-sm rounded"
                                                onClick={() => navigate(`/jobdetails`, { state: { job: relatedJob } })}
                                                style={{ cursor: 'pointer', fontSize:'13px', marginLeft:'11px' }}
                                            >
                                                {/* Image Section */}
                                                <div className="jobcard-image text-center" style={{ width: '30%', flexShrink: 0 }}>
                                                    <img
                                                        src={relatedJob.profile_photo_url || '/fallback-user.png'}
                                                        alt={relatedJob.job_title}
                                                        className="img-fluid rounded-circle jobcard-avatar-eviewjobs mb-2 viewjob-image-viewjob"
                                                        style={{ width: '80px', height: '80px', objectFit: 'cover' }}
                                                    />
                                                    <div className="jobcard-label-text">
                                                        {relatedJob.posted_by?.toLowerCase().includes('direct') ? 'Employer' : 'Agency'}
                                                    </div>
                                                </div>

                                                {/* Content Section */}
                                                <div className="jobcard-content ms-3" style={{ width: '70%' }}>
                                                    <div className="jobcard-title-container mb-2">
                                                        <h6 className="jobcard-title-viewalljobs mb-0">
                                                            {relatedJob.job_title}
                                                        </h6>
                                                        {/* Gold and Silver Stars */}
                                                        <div className="jobcard-stars-container">
                                                            {hasGoldSubscription(relatedJob) && (
                                                                <span className="star-icon" title="Gold Subscription Employer">
                                                                    <i className="bi bi-star-fill text-warning"></i>
                                                                </span>
                                                            )}
                                                            {hasSilverSubscription(relatedJob) && (
                                                                <span className="star-icon" title="Silver Subscription Employer">
                                                                    <i className="bi bi-star-fill text-secondary"></i>
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="jobcard-family text-muted mb-1" style={{ fontSize: '12px' }}>
                                                        {relatedJob.adults || 'N/A'} adults + {relatedJob.children || 'N/A'} kids | {relatedJob.nationality || 'N/A'}
                                                    </div>
                                                    <div className="jobcard-location-alljobs text-muted mb-1" style={{ fontSize: '12px' }}>
                                                        <i className="bi bi-geo-alt-fill me-1"></i>
                                                        {relatedJob.working_city || 'N/A'}, {relatedJob.state_or_province || 'N/A'}
                                                    </div>
                                                    {/* Job Description - Hidden on mobile, visible on desktop */}
                                                    <div className="viewjobs-jobcard-description text-muted mb-2" style={{ fontSize: '12px', lineHeight: '1.3' }}>
                                                        {relatedJob.job_description ?
                                                            (relatedJob.job_description.length > 80
                                                                ? relatedJob.job_description.substring(0, 80) + '...'
                                                                : relatedJob.job_description)
                                                            : 'No description available'
                                                        }
                                                    </div>
                                                    <div className="d-flex flex-column gap-1 small mt-1">
                                                        <div className="text-muted" style={{ fontSize: '11px' }}>
                                                            <i className="bi bi-person-fill me-1"></i>
                                                            {relatedJob.domestic_worker_category || 'N/A'} | {relatedJob.job_type || 'N/A'}
                                                        </div>
                                                        <div className="text-muted" style={{ fontSize: '11px' }}>
                                                            <i className="bi bi-calendar-event me-1"></i>
                                                            <strong>Joining:</strong> {formatDate(relatedJob.job_starting_date)}
                                                        </div>
                                                        <div className="text-success fw-semibold" style={{ fontSize: '11px' }}>
                                                            <i className="bi bi-circle-fill me-1"></i>
                                                            Very Active
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <p>No similar jobs found</p>
                                )}
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>

            <Footer />
        </>
    );
}

export default JobDetails;